// OrganizationDataEditPage
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Stack,
  Textarea,
  Avatar,
  useToast,
  Divider,
  Spinner,
  Icon,
} from "@chakra-ui/react";
import { ElementType, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiSave, FiArrowLeft } from "react-icons/fi";
import { MdCloudUpload } from "react-icons/md";
import { getOrganization, updateOrganization } from "../hooks/org_data";
import { Organization } from "../utils/types";

export default function EditOrganizationPage() {
  const { orgId } = useParams();
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<string | undefined>();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrg();
  }, []);

  const fetchOrg = async () => {
    setLoading(true);
    try {
      const res = await getOrganization(orgId!);
      setOrg(res.data);
      setPreview(res.data?.logo_url);
    } catch (err) {
      toast({
        title: "Failed to load organization data.",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof Organization, value: string) => {
    setOrg((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    // In a real app, you would upload the file to your backend or storage service
  };

  const handleSave = async () => {
    if (!org) return;
    setSaving(true);
    try {
      await updateOrganization(orgId!, org);
      toast({
        title: "Organization updated successfully!",
        status: "success",
      });
      navigate(`/organization/${orgId}/info`);
    } catch {
      toast({
        title: "Failed to update organization.",
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Flex justify="center" align="center" h="60vh">
        <Spinner size="xl" />
      </Flex>
    );

  return (
    <Box maxW="800px" mx="auto" p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Edit Organization</Heading>
        <Button
          leftIcon={<Icon as={FiArrowLeft as ElementType} />}
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Flex>

      <Divider mb={6} />

      <Stack spacing={5}>
        <FormControl>
          <FormLabel>Logo</FormLabel>
          <Flex align="center" gap={4}>
            <Avatar size="xl" bg={"#006400"} color={"white"} src={preview} name={org?.name} />
            <Button
              leftIcon={<Icon as={MdCloudUpload as ElementType} />}
              onClick={() => document.getElementById("logo-upload")?.click()}
              variant="outline"
            >
              Upload Logo
            </Button>
            <Input
              id="logo-upload"
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
          </Flex>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Organization Name</FormLabel>
          <Input
            value={org?.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Organization Description</FormLabel>
          <Input
            value={org?.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Industry</FormLabel>
          <Select
            value={org?.industry || ""}
            onChange={(e) => handleChange("industry", e.target.value)}
          >
            <option value="">Select industry</option>
            <option value="Technology">Technology</option>
            <option value="Education">Education</option>
            <option value="Finance">Finance</option>
            <option value="Retail">Retail</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Other">Other</option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            value={org?.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Phone</FormLabel>
          <Input
            value={org?.phone_number || ""}
            onChange={(e) => handleChange("phone_number", e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Website</FormLabel>
          <Input
            value={org?.website || ""}
            onChange={(e) => handleChange("website", e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Address</FormLabel>
          <Textarea
            rows={3}
            value={org?.address || ""}
            onChange={(e) => handleChange("address", e.target.value)}
          />
        </FormControl>

        <Flex justify="flex-end" gap={3} mt={4}>
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            bg={'#006400'}
            leftIcon={<Icon as={FiSave as ElementType} />}
            onClick={handleSave}
            isLoading={saving}
          >
            Save Changes
          </Button>
        </Flex>
      </Stack>
    </Box>
  );
}
